import React, { useState } from 'react';
import { useHistory, Switch, Route } from 'react-router-dom';
import {
  CssBaseline,
  Container,
  Grid,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  Divider,
  Badge,
  Menu,
  MenuItem,
  Box
} from '@material-ui/core';
import { ChevronLeft, Notifications, AccountCircle, Menu as MenuIcon } from '@material-ui/icons';
import clsx from 'clsx';
import useStyle from './useStyle';
import SidebarMenuItems from './SidebarMenuItems';
import { Version, Toaster, Loading } from '@/components';
import { useAuth } from '@/auth/AuthProvider';
import { Dashboard, ErrorScreenPermission } from '@/screens';
import UserContext from '@/contexts/UserContext';
import WoodMark from '@/assets/img/Wordmark@2x.png';
import { MainContainerLayoutProps } from '@/types/generals';
import { PRIVATE_ROUTES_COMPLETE } from '@/navigation/privateRoutes';
import { useCurrentRouteTitle } from '@/hooks/useGetRouteTitle';

const MainContainerLayout: React.FC<MainContainerLayoutProps> = ( ) => {
  const classes = useStyle();
  const history = useHistory();
  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [alert, setAlert] = useState({ status: false, type: '', message: '' });
  const { user, permissions, loading, error, signOut, hasPermission } = useAuth();

  const title = useCurrentRouteTitle();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (action: string) => {
    setAnchorEl(null);
    if (action === 'exit') {
      signOut();
      history.push('/login');
    }
  };

  if (loading) return <Loading />;
  if (error || !user) return <ErrorScreenPermission />;

  return (
    <UserContext.Provider
      value={{
        user,
        permissions,
        error,
        signOut,
        hasPermission
      }}
    >
      <div className={classes.root}>
        <CssBaseline />

        <Toaster
          show={alert.status}
          type={alert.type}
          text={alert.message}
          onClose={() => setAlert({ ...alert, status: false })}
        />

        <AppBar position="absolute" className={clsx(classes.appBar, open && classes.appBarShift)} elevation={0}>
          <Toolbar className={classes.toolbar}>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="open drawer"
              onClick={() => setOpen(true)}
              className={clsx(classes.menuButton, open && classes.menuButtonHidden)}
            >
              <MenuIcon />
            </IconButton>

            <Typography component="h1" variant="h6" color="inherit" noWrap className={classes.title}>
              {title || 'Dashboard - ⚠ Data de ejemplo ⚠'}
            </Typography>

            <IconButton color="inherit">
              <Badge badgeContent={0} color="secondary">
                <Notifications />
              </Badge>
            </IconButton>

            <IconButton color="inherit" onClick={handleClick}>
              <AccountCircle />
            </IconButton>

            <Menu anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem>
                <AccountCircle /> &nbsp; {user.username}
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => handleClose('exit')}>Salir</MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Drawer
          variant="permanent"
          classes={{ paper: clsx(classes.drawerPaper, !open && classes.drawerPaperClose) }}
          open={open}
        >
          <div className={classes.toolbarIcon}>
            <img src={WoodMark} alt="billet" height="35px" />
            <Typography variant="subtitle1">Backoffice</Typography>
            <IconButton onClick={() => setOpen(false)}>
              <ChevronLeft />
            </IconButton>
          </div>
          <Divider />
          <SidebarMenuItems />
        </Drawer>

        <main className={classes.content}>
          <div className={classes.appBarSpacer} />
          <Container maxWidth="xl" className={classes.container}>
            <Grid item xs={12}>
              <Switch>
                {Object.entries(PRIVATE_ROUTES_COMPLETE).flatMap(([key, config]) => {
                  const routes = [];

                  // Rutas sin secciones (como dashboard)
                  if (!config.sections || config.sections.length === 0) {
                    const perms = hasPermission(key);
                    if (perms.noScreen) return [];

                    routes.push(
                      <Route
                        key={key}
                        exact
                        path={`/${config.navigation}`}
                        render={() => {
                          if (perms.canWrite) {
                            return <>{config.screen}</>;
                          }
                          return config.screenReadOnly ? <>{config.screenReadOnly}</> : <ErrorScreenPermission />;
                        }}
                      />
                    );
                  }

                  // Rutas con secciones (como administrator, etc.)
                  if (config.sections) {
                    config.sections.forEach(section => {
                      const perms = hasPermission(section.navigation.replace('/', ''));
                      if (perms.noScreen) return;

                      routes.push(
                        <Route
                          key={section.navigation}
                          exact
                          path={`/${section.navigation}`}
                          render={() => {
                            if (perms.canWrite) {
                              return <>{section.screen}</>;
                            }

                            return section.screenReadOnly ? <>{section.screenReadOnly}</> : <ErrorScreenPermission />;
                          }}
                        />
                      );
                    });
                  }
                  return routes;
                })}
                <Route path="/" component={Dashboard} />
                <Route path="*" component={ErrorScreenPermission} />
              </Switch>
            </Grid>
            <Box pt={4}>
              <Version />
            </Box>
          </Container>
        </main>
      </div>
    </UserContext.Provider>
  );
};

export default MainContainerLayout;
